import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import { TOnClick } from "components/ProjectSpecificComponents/Interfaces/GlobalInterface"
import React, { useEffect, useState } from "react"
import ModalWithSepLine from "./ModalWithSepLine"
import HorizontalFormField from "../../../AdminForm/molecules/HorizontalFormField"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import { useTranslation } from "react-i18next"
import { IFormsConfig } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import Checkbox from "../../atoms/Checkbox"
import PreferencesSmallButton from "../../atoms/Buttons/PreferencesSmallButton"
import isNumber from "components/ProjectNonSpecificComponents/Functions/isNumber"
import { IptkmFormConfig } from "components/ProjectSpecificComponents/Stores/PTKMStore/ptkmStore"
import { usePTKMStore } from "components/ProjectSpecificComponents/Contexts/PTKMStoreContext"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { IJamaProjectsAndFolders } from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import disableEmptyProjects from "components/ProjectSpecificComponents/Elements/AdminForm/atoms/disableEmptyProjects"
import { observer } from "mobx-react"
import Select from "../../atoms/Select"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import { isReactSelectSingleOption } from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import CopyConfigFromList from "components/ProjectSpecificComponents/Elements/CopyConfig/CopyConfigFromList"
import CopyIcon from "components/ProjectSpecificComponents/Elements/Global/atoms/Icons/CopyIcon"
import Tooltip from "../../atoms/Tooltip/Tooltip"
import ReactTooltip from "react-tooltip"
import getUrlToken from "components/ProjectNonSpecificComponents/Functions/getUrlToken"
import isURLRegex from "components/ProjectSpecificComponents/Functions/isURLRegex"

interface Props {
	onClose?: TOnClick<HTMLButtonElement | HTMLElement>
	onSave?: (data: Partial<IBothConfigs>) => void
	configData: Partial<IBothConfigs>
	modalTitle?: string
	modalButtonName?: string
	type: "PTKM" | "REQUEST"
	newConfig?: boolean
}

interface IBothConfigs extends IptkmFormConfig, IFormsConfig {}

const NewConfigurationModal = observer(
	({
		onClose = () => {},
		configData,
		onSave = () => {},
		modalTitle = "",
		modalButtonName = "",
		type,
		newConfig = false,
	}: Props) => {
		const { t } = useTranslation()
		const ptkmStore = usePTKMStore()
		const store = useStore()
		const [configInfo, setConfigInfo] = useState<Partial<IBothConfigs>>(configData)
		const [alerts, setAlerts] = useState<{ [key: string]: boolean }>({})
		const position = configInfo?.position ?? 0

		const saveAndClose = (e: React.MouseEvent<HTMLButtonElement | HTMLElement>) => {
			const inputAlerts = checkInputFields()
			// If object length is bigger than 0 then return
			if (Object.entries(inputAlerts).length > 0) return

			onSave(configInfo)
			onClose(e)
		}

		const checkInputFields = () => {
			const isPTKM = type === "PTKM"
			let foundAlerts = {}

			if (!configInfo.name?.trim())
				foundAlerts = {
					...foundAlerts,
					name: true,
				}

			if (configInfo?.nameURL) {
				if (!isURLRegex(configInfo?.nameURL)) {
					foundAlerts = {
						...foundAlerts,
						nameURL: true,
					}
				}
			}

			if (isPTKM && !ptkmStore.copiedConfig && !configInfo.project?.trim())
				foundAlerts = {
					...foundAlerts,
					project: true,
				}

			if (isPTKM && !ptkmStore.copiedConfig && !configInfo.item?.trim())
				foundAlerts = {
					...foundAlerts,
					item: true,
				}

			setAlerts(foundAlerts)
			return foundAlerts
		}

		const clearAlert = (key: string) => {
			setAlerts({ ...alerts, [key]: false })
		}

		const changePosition = (e: React.ChangeEvent<HTMLInputElement>) => {
			// For user id's must start from 1 but in store indexes starts from 0.
			// Depending on user input value is parsed and/or subtracted
			const parsedValue = parseInt(e.target.value) == 0 ? 0 : parseInt(e.target.value) - 1 || 0

			setConfigInfo({
				...configInfo,
				position: parsedValue,
			})
		}

		useEffect(() => {
			configInfo?.item && getProjectsFromItem(JSON.parse(configInfo.item).value)
		}, [configInfo?.item])

		const getProjectsFromItem = async (requestedItemId: string) => {
			store.startInputLoading("project")
			const { ok, data } = await AppRequests("Collection").get<IJamaProjectsAndFolders[]>(
				"allProjectsWithFolders",
				{ requestedItemId }
			)
			if (ok && data) {
				const disabledUnavailableOptions = await disableEmptyProjects(data)
				ptkmStore.setAllProjects(disabledUnavailableOptions)
			}
			store.stopInputLoading("project")
		}

		const handleCopy = (e: any) => {
			ReactTooltip.hide()
			let token = getUrlToken()
			let url = window.location.href
				.replace("request", `?form=${configInfo.nameURL}`)
				.replace(`?token=${token}`, "")
			switch (e?.type) {
				case "copy":
					e.preventDefault()
					e.clipboardData.setData("text/plain", url)
					break
				case "click":
					navigator.clipboard.writeText(url)
					break
			}
		}

		return (
			<div>
				<ModalWithSepLine title={modalTitle} onClose={onClose}>
					<Whitespace x="1" />
					<HyperDiv direction="column" padding=".5rem 0">
						<HorizontalFormField
							containerStyle={{ padding: "8px 16px 20px 16px" }}
							label={t(`forms.field-label.name`)}
							centerSpaceMultiplier={0.5}
							tooltip={t("forms.tooltip.field-description.config-name")}
							tooltipBelowInputField
						>
							<Whitespace h x="1" />
							<Input
								style={{ border: alerts?.name ? "1px solid var(--fail)" : undefined }}
								wide
								value={configInfo?.name ?? ""}
								onChange={(e) => setConfigInfo({ ...configInfo, name: e.target.value })}
								onKeyPress={() => clearAlert("name")}
							/>
							{alerts?.name && (
								<HyperDiv className="font-size-16" style={{ color: "var(--fail)" }}>
									{t(`inputs.name-required`)}
								</HyperDiv>
							)}
						</HorizontalFormField>
						{type === "REQUEST" && (
							<HorizontalFormField
								containerStyle={{ padding: "8px 16px 20px 16px" }}
								label={t(`forms.field-label.name-url`)}
								centerSpaceMultiplier={0.5}
								tooltip={t("forms.tooltip.field-description.url-name")}
								tooltipBelowInputField
							>
								<Whitespace h x="1" />
								<HyperDiv direction="row" className="center-y">
									<Input
										style={{ border: alerts?.name ? "1px solid var(--fail)" : undefined }}
										wide
										value={configInfo?.nameURL ?? ""}
										onChange={(e) => setConfigInfo({ ...configInfo, nameURL: e.target.value })}
										onCopy={handleCopy}
										onKeyPress={() => clearAlert("nameURL")}
									/>
									<Whitespace h x="1" />
									<>
										<div
											onClick={handleCopy}
											className="pointer center-y"
											data-for="CopyInfo"
											data-tip={t(`forms.tooltip.copy`)}
										>
											<CopyIcon height={32} width={32} />
										</div>
										<Tooltip id="CopyInfo" type="dark" />
									</>
								</HyperDiv>
								{alerts?.nameURL && (
								<HyperDiv className="font-size-16" style={{ color: "var(--fail)" }}>
									{t(`inputs.url-incorrect`)}
								</HyperDiv>
							)}
							</HorizontalFormField>
						)}
						{newConfig && !configInfo.item && !configInfo.project && (
							<CopyConfigFromList type={type} />
						)}

						{type === "PTKM" && !ptkmStore.copiedConfig && (
							<>
								<HorizontalFormField
									containerStyle={{ padding: "20px 16px" }}
									label={t(`forms.field-label.item`)}
									centerSpaceMultiplier={0.5}
									// ! TECH DEBT : DEFINED TOOLTIP
									// tooltip={t("forms.tooltip.field-description.config-item")}
									tooltipBelowInputField
								>
									<Select
										options={ptkmStore.allItems}
										valueKey={configInfo.item ? JSON.parse(configInfo.item).value : ""}
										onChange={(selectedOption) => {
											clearAlert("item")
											setConfigInfo(
												isReactSelectSingleOption(selectedOption)
													? {
															...configInfo,
															itemId: selectedOption.value,
															item: JSON.stringify({
																value: selectedOption.value,
																label: selectedOption.label,
															}),
															project: "",
													  }
													: {
															...configInfo,
															itemId: undefined,
															item: undefined,
															project: undefined,
													  }
											)
										}}
									/>
									{alerts?.item && (
										<HyperDiv className="font-size-16" style={{ color: "var(--fail)" }}>
											{t(`inputs.item-required`)}
										</HyperDiv>
									)}
								</HorizontalFormField>
								<Whitespace x="" />

								<HorizontalFormField
									containerStyle={{ padding: "20px 16px" }}
									label={t(`forms.field-label.project`)}
									centerSpaceMultiplier={0.5}
									// ! TECH DEBT : DEFINED TOOLTIP
									// tooltip={t("forms.tooltip.field-description.config-project")}
									tooltipBelowInputField
								>
									<Select
										isDisabled={!configInfo.item}
										isLoading={!!store.inputIsLoading["project"]}
										options={ptkmStore.allProjects}
										valueKey={configInfo.project ? JSON.parse(configInfo.project).value : ""}
										onChange={(selectedOption) => {
											clearAlert("project")
											setConfigInfo(
												isReactSelectSingleOption(selectedOption)
													? {
															...configInfo,
															projectId: selectedOption.value,
															project: JSON.stringify({
																value: selectedOption.value,
																label: selectedOption.label,
															}),
													  }
													: { ...configInfo, projectId: undefined, project: undefined }
											)
										}}
									/>
									{alerts?.project && (
										<HyperDiv className="font-size-16" style={{ color: "var(--fail)" }}>
											{t(`inputs.project-required`)}
										</HyperDiv>
									)}
								</HorizontalFormField>
							</>
						)}
						<HorizontalFormField
							containerStyle={{ padding: "20px 16px" }}
							label={t(`forms.field-label.number`)}
							centerSpaceMultiplier={0.5}
							tooltip={t("forms.tooltip.field-description.config-number")}
							tooltipBelowInputField
						>
							<Input
								wide
								defaultValue={position + 1}
								onChange={changePosition}
								onKeyPress={(event) => {
									if (!isNumber(event.key)) {
										event.preventDefault()
									}
								}}
							/>
						</HorizontalFormField>
						{(type === "REQUEST" || configInfo.isActive) && (
							<HorizontalFormField
								containerStyle={{ padding: "20px 16px" }}
								label={t(`forms.field-label.active`)}
								centerSpaceMultiplier={0.5}
								tooltip={t("forms.tooltip.field-description.config-active")}
								tooltipBelowInputField
							>
								<Checkbox
									checked={configInfo.isActive}
									onCheck={() =>
										(type !== "PTKM" || configInfo.isActive) &&
										setConfigInfo({ ...configInfo, isActive: !configInfo.isActive })
									}
								/>
							</HorizontalFormField>
						)}
					</HyperDiv>
					<Whitespace x="1" />
					<HyperDiv centeredX>
						<PreferencesSmallButton
							style={{ width: "25%" }}
							onClick={saveAndClose}
							children={modalButtonName}
						/>
					</HyperDiv>
				</ModalWithSepLine>
			</div>
		)
	}
)

export default NewConfigurationModal
